import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import queryClient from '../../utils/queryClient';
import OrderAPI, {
  type AddToCartPayload,
  type ExecutePaymentArgs,
  type PaymentOptionsPayload,
  type RemoveFromCartPayload,
  type SetPaymentOptionPayload,
  type SetShippingOptionPayload,
  type ShippingOptionsPayload,
  type UpdateCartItemPayload,
} from '../fetch/orderApi';
import notify from '../../utils/notify';
import { type ApiError } from '../fetch/apiClient';
import { apiErrorKey } from '../../utils/apiErrorKey';
import type { Paginated } from '../pagination';
import type {
  Cart,
  CartItem,
  CheckoutResponse,
  DeliveryPoint,
  ExecutePaymentResponse,
  Order,
  OrderListRow,
  PaymentOption,
  ShippingOption,
  Uuid,
} from '../types';
import type { ApiAddress } from '../../utils/addressUtils';

export const useAddToCart = () =>
  useMutation<CartItem, ApiError, AddToCartPayload>({
    mutationFn: OrderAPI.addToCart,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cart'] });
    },
    onError: (error) => {
      notify('notify.error', apiErrorKey(error, 'notify.orders.add_to_cart_failed'), {
        type: 'warning',
      });
    },
  });

export const useUpdateCartItem = () =>
  useMutation<CartItem, ApiError, UpdateCartItemPayload>({
    mutationFn: OrderAPI.updateCartItem,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cart'] });
    },
    onError: () => {
      notify('notify.error', 'notify.orders.update_cart_failed', { type: 'warning' });
    },
  });

export const useRemoveFromCart = () =>
  useMutation<void, ApiError, RemoveFromCartPayload>({
    mutationFn: OrderAPI.removeFromCart,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cart'] });
    },
    onError: () => {
      notify('notify.error', 'notify.orders.remove_from_cart_failed', { type: 'warning' });
    },
  });

export const useSetShippingOption = () =>
  useMutation<Cart, ApiError, SetShippingOptionPayload>({
    mutationFn: OrderAPI.setShippingOption,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cart'] });
      // Per-option delivery projections (Fulfillment::OptionProjections) key
      // off the cart's selected shipping/payment option, so switching one
      // stales both option lists' projected_delivery ranges — invalidate
      // them alongside cart rather than leaving them to their own 30s
      // staleTime.
      queryClient.invalidateQueries({ queryKey: ['shippingMethods'] });
      queryClient.invalidateQueries({ queryKey: ['paymentMethods'] });
    },
    onError: () => {
      notify('notify.error', 'notify.orders.set_shipping_method_failed', { type: 'warning' });
    },
  });

export const useSetPaymentOption = () =>
  useMutation<Cart, ApiError, SetPaymentOptionPayload>({
    mutationFn: OrderAPI.setPaymentOption,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cart'] });
      // See useSetShippingOption above: the same cross-dependency applies in
      // the other direction — a payment-option change stales both projected
      // shipping and payment delivery ranges.
      queryClient.invalidateQueries({ queryKey: ['shippingMethods'] });
      queryClient.invalidateQueries({ queryKey: ['paymentMethods'] });
    },
    onError: () => {
      notify('notify.error', 'notify.orders.set_payment_method_failed', { type: 'warning' });
    },
  });

export const useCart = () =>
  useQuery<Cart>({
    queryKey: ['cart'],
    queryFn: OrderAPI.getCart,
  });

export const useShippingOptions = (filter: ShippingOptionsPayload) => {
  const { i18n } = useTranslation();
  return useQuery<ShippingOption[]>({
    queryKey: ['shippingMethods', filter, i18n.language],
    queryFn: () => OrderAPI.getShippingOptions(filter),
    enabled: Array.isArray(filter.album_ids) && filter.album_ids.length > 0,
  });
};

export const useDeliveryPoints = (
  shippingOptionId: Uuid | null,
  country: string,
  query: string,
) =>
  useQuery<{ delivery_points: DeliveryPoint[] }>({
    queryKey: ['deliveryPoints', shippingOptionId, country, query],
    queryFn: () =>
      OrderAPI.getDeliveryPoints({ shipping_option_id: shippingOptionId as Uuid, country, query }),
    enabled: !!shippingOptionId,
    staleTime: 5 * 60 * 1000,
  });

export const usePaymentMethods = (filter: PaymentOptionsPayload, options?: { enabled?: boolean }) => {
  const { i18n } = useTranslation();
  return useQuery<PaymentOption[]>({
    queryKey: ['paymentMethods', filter, i18n.language],
    queryFn: () => OrderAPI.getPaymentOptions(filter),
    enabled: options?.enabled ?? true,
  });
};

export const useAddresses = (address_type: 'billing' | 'shipping' = 'billing') =>
  useQuery<ApiAddress[]>({
    queryKey: ['addresses', address_type],
    queryFn: () => OrderAPI.getAddresses({ address_type }),
  });

export const useCheckout = () =>
  useMutation<CheckoutResponse, ApiError, { terms_accepted: boolean }>({
    mutationFn: (body) => OrderAPI.checkout(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cart'] });
      queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
    onError: (error) => {
      notify('notify.error', apiErrorKey(error, 'notify.orders.checkout_failed'), {
        type: 'warning',
      });
    },
  });

export const useOrders = (page: number = 1) =>
  useQuery<Paginated<OrderListRow>>({
    queryKey: ['orders', 'self_only', page],
    queryFn: () => OrderAPI.getOrders({ self_only: true, page }),
    placeholderData: keepPreviousData,
  });

export const useOrder = (orderId: Uuid | null | undefined) => {
  const { i18n } = useTranslation();
  return useQuery<Order>({
    queryKey: ['order', orderId, i18n.language],
    queryFn: () => OrderAPI.getOrder(orderId as Uuid),
    enabled: !!orderId,
  });
};

export const useExecutePayment = () =>
  useMutation<ExecutePaymentResponse, ApiError, ExecutePaymentArgs>({
    mutationFn: OrderAPI.executePayment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
  });

export const useCancelPayment = () =>
  useMutation<Order, ApiError, Uuid>({
    mutationFn: OrderAPI.cancelPayment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
  });

// The options a buyer may switch to on an existing order (#98 task 13c),
// backing the retry/switch selector on OrderDetails.tsx. Only fetch while
// there's actually a Pay-now affordance to select for — callers pass
// `undefined` otherwise, which `enabled` turns into a no-op. No onError
// callback (TanStack Query v5 dropped that option from useQuery); the
// consumer reads `isError` and renders a fallback instead, matching this
// codebase's convention for query (as opposed to mutation) failures.
export const useOrderPaymentOptions = (orderId: Uuid | null | undefined) => {
  const { i18n } = useTranslation();
  return useQuery<PaymentOption[]>({
    queryKey: ['orderPaymentOptions', orderId, i18n.language],
    queryFn: () => OrderAPI.orderPaymentOptions(orderId as Uuid),
    enabled: !!orderId,
  });
};

export interface StartOrderPaymentArgs {
  orderId: Uuid;
  paymentOptionId?: Uuid;
}

// Starts/retries payment on an existing order (#98 recovery path). A manual
// method (bank transfer/cash/custom) resolves successfully with
// requires_redirect: false and a reference that is a string for bank
// transfer but null for cash/custom — that null is not an error, so this
// hook (like the type it returns) must not special-case it.
export const useStartOrderPayment = () =>
  useMutation<CheckoutResponse, ApiError, StartOrderPaymentArgs>({
    mutationFn: ({ orderId, paymentOptionId }) => OrderAPI.startOrderPayment(orderId, paymentOptionId),
    onSuccess: (_data, { orderId }) => {
      queryClient.invalidateQueries({ queryKey: ['order', orderId] });
      queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
    onError: (error) => {
      notify('notify.error', apiErrorKey(error, 'notify.orders.payment_start_failed'), {
        type: 'warning',
      });
    },
  });
